import { useCallback } from "react";

import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  ForecastActivityType,
  MCDANIEL_FOLDER_ID,
  NOVI_FOLDER_ID,
  TYPE_WELLS,
  USER_ARPS,
  WORKING_FORECAST_FOLDER_ID,
  WORKING_FORECAST_FOLDER_NAME
} from "constants/settings.constants";
import { SPECIAL_FOLDERS_ID } from "store/features";
import {
  selectCheckedForecastKeys,
  selectCheckedTypeWellKeys,
  selectUseMcDanielForecasts,
  selectUseNoviForecasts,
  selectUseWorkingForecasts,
  useForecastStore
} from "stores/forecast";
import { convertForecastConstantUnits } from "utils/arps/convertForecastConstantUnits";
import { toExpectedDisplayName } from "utils/arps/productMapping";

import { ForecastFolder, ForecastFolderItem, ForecastItem } from "models/UserArpsModel";

import { ORG_FORECAST_ID } from "components/org-settings/constants/constants";

import useForecastPreferenceQuery from "../queries/useForecastPreferenceQuery";
import { getDurationInMonths } from "../utils/dates";
import { groupArpsSegmentsByProduct } from "../utils/groupArpsSegmentsByProduct";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Groups an array of items by a specified key
 */
function groupByKey<T>(items: T[], key: keyof T): Record<string, T[]> {
  return items.reduce((result, item) => {
    const groupKey = String(item[key]);
    (result[groupKey] = result[groupKey] || []).push(item);
    return result;
  }, {} as Record<string, T[]>);
}

/**
 * Sorts items by order (ascending), then by name (alphabetically) for ties
 */
function sortByOrderThenName<T extends { order?: number; name: string }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const orderComparison = (a.order ?? 0) - (b.order ?? 0);
    if (orderComparison === 0) {
      return a.name.localeCompare(b.name);
    }
    return orderComparison;
  });
}

/**
 * Sorts folders by priority: User folders first, then Org folders, then Special folders
 */
function sortFoldersByPriority(folders: ForecastFolder[]): ForecastFolder[] {
  return [...folders].sort((a, b) => {
    const aIsSpecial = SPECIAL_FOLDERS_ID.includes(a.folderId);
    const bIsSpecial = SPECIAL_FOLDERS_ID.includes(b.folderId);
    const aIsOrg = a.projectId === ORG_FORECAST_ID;
    const bIsOrg = b.projectId === ORG_FORECAST_ID;

    // User folders (not org, not special) come first
    const aIsUser = !aIsOrg && !aIsSpecial;
    const bIsUser = !bIsOrg && !bIsSpecial;

    if (aIsUser && !bIsUser) return -1;
    if (!aIsUser && bIsUser) return 1;

    // Org folders come second
    if (aIsOrg && !aIsSpecial && bIsSpecial) return -1;
    if (bIsOrg && !bIsSpecial && aIsSpecial) return 1;

    // Keep existing order within groups (stable sort)
    return 0;
  });
}

/**
 * Transforms a single TYPE_WELLS forecast with arps segments and constants
 */
function transformTypeWellForecast(forecast: ForecastItem) {
  // Deep copy the forecast to avoid mutating the original data
  const copiedForecast = JSON.parse(JSON.stringify(forecast));
  let segments = [];

  const arpsGroupedByProduct = groupArpsSegmentsByProduct(copiedForecast.arps ?? []);

  for (const product of Object.keys(arpsGroupedByProduct)) {
    const productSegments = arpsGroupedByProduct[product].map((segment) => ({
      ...segment,
      product: toExpectedDisplayName(segment.product)
    }));

    if (productSegments.length > 2) {
      const seg1 = productSegments[0];
      // Set switch month for 3 segments
      const switchMonth =
        seg1.switchMonth ??
        getDurationInMonths(new Date(seg1.startDate), new Date(seg1.endDate));
      segments = segments
        .concat([{ ...seg1, switchMonth }])
        .concat([...productSegments.slice(1)]);
    } else {
      segments = segments.concat(productSegments);
    }
  }

  copiedForecast.constants = copiedForecast.constants?.map((constant) => ({
    ...constant,
    product: toExpectedDisplayName(constant.product)
  }));

  const constantsWithUnits = copiedForecast.constants
    ? convertForecastConstantUnits(copiedForecast.constants)
    : [];

  return {
    ...copiedForecast,
    arps: segments,
    constants: constantsWithUnits
  };
}

/**
 * Creates an empty ForecastFolder from folder data
 */
function createEmptyFolder(folder: {
  folderId: string;
  name: string;
  projectId: string;
  parentId: string;
  order: number;
}): ForecastFolder {
  return new ForecastFolder({
    folderId: folder.folderId,
    reserveCategory: null,
    name: folder.name,
    folderName: folder.name,
    projectId: folder.projectId,
    forecasts: [],
    parentId: folder.parentId,
    order: folder.order
  });
}

/**
 * Processes a USER_ARPS folder, grouping forecasts into virtual folders by reserve category.
 * Non-expandability for large folders (>=1000 forecasts) and org folders is enforced in the
 * UI layer via isFolderExpansionDisabled, so rescat grouping is always applied here.
 */
function processUserArpsFolder(folder): ForecastFolder[] {
  const folders: ForecastFolder[] = [];

  // Always group by reserve category so each rescat appears as its own virtual folder.
  // Expansion limits are enforced in the UI (isFolderExpansionDisabled), not here.
  const grouped = groupByKey(folder.forecasts, "reserveCategory");

  for (const [reserveCategory, forecasts] of Object.entries(grouped)) {
    const folderItem: ForecastFolderItem = {
      folderId: folder.folderId,
      reserveCategory: reserveCategory,
      folderName: folder.name,
      name: folder.name,
      projectId: folder.projectId,
      forecasts: sortByOrderThenName(forecasts as ForecastItem[]),
      parentId: folder.parentId,
      order: folder.order
    };

    folders.push(new ForecastFolder(folderItem));
  }

  return folders;
}

/**
 * Processes a TYPE_WELLS folder, transforming forecasts with arps segments and constants
 */
function processTypeWellsFolder(folder): ForecastFolder {
  const transformedForecasts = (folder.forecasts as ForecastItem[]).map(
    transformTypeWellForecast
  );

  const folderItem: ForecastFolderItem = {
    reserveCategory: "",
    folderId: folder.folderId,
    folderName: folder.name,
    name: folder.name,
    projectId: folder.projectId,
    forecasts: sortByOrderThenName(transformedForecasts),
    parentId: folder.parentId,
    order: folder.order
  };

  return new ForecastFolder(folderItem);
}

/**
 * Creates special folder entries (McDaniel, Novi, Working Forecasts) based on user preferences
 */
function getSpecialFoldersFromPreferences(forecastPreference): ForecastFolder[] {
  if (!forecastPreference) return [];

  const sortedData = [...forecastPreference].sort((a, b) => a.order - b.order);

  const forecastSources = {
    McDaniel: {
      folderId: MCDANIEL_FOLDER_ID,
      name: EVA_RESEARCH_NAME
    },
    [WORKING_FORECAST_FOLDER_NAME]: {
      folderId: WORKING_FORECAST_FOLDER_ID,
      name: WORKING_FORECAST_FOLDER_NAME
    },
    Novi: {
      folderId: NOVI_FOLDER_ID,
      name: "Novi Labs"
    }
  };

  const specialFolders: ForecastFolder[] = [];

  for (const source of sortedData) {
    const dataSourceDetails = forecastSources[source.dataSourceName];
    if (dataSourceDetails) {
      specialFolders.push(
        new ForecastFolder({
          folderId: dataSourceDetails.folderId,
          reserveCategory: null,
          name: dataSourceDetails.name,
          folderName: "",
          projectId: "",
          forecasts: [],
          parentId: "",
          order: 0
        })
      );
    }
  }

  return specialFolders;
}

// ============================================================================
// Hook
// ============================================================================

export default function useForecastFolderFetcher(type) {
  // Each row uses this hook, and as each row is added to the virtual list on scroll,
  // it will create a new query and refetches on mount which is not ideal, a stale time will help reduce frequency,
  const forecastPreference = useForecastPreferenceQuery({ staleTime: 20000 }).data;

  // ForecastStore selectors
  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const checkedForecastKeys = useForecastStore(selectCheckedForecastKeys);
  const checkedKeys = type === TYPE_WELLS ? checkedTypeWellKeys : checkedForecastKeys;
  const useMcDanielForecasts = useForecastStore(selectUseMcDanielForecasts);
  const useNoviForecasts = useForecastStore(selectUseNoviForecasts);
  const useWorkingForecasts = useForecastStore(selectUseWorkingForecasts);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);

  /**
   * Syncs checked keys for special folders based on user preferences.
   * Updates ForecastStore if a special folder should be checked but isn't yet.
   */
  const syncSpecialFolderCheckedKeys = useCallback(
    (type: ForecastActivityType) => {
      // TODO BF: Not sure if this is the right place to do this, I think the intention was to initialize the checked keys in the project forecasts,
      // but now this hook is being used in other places as well, but I can't foresee negative side effects atm.
      const setKeys = type === TYPE_WELLS ? setCheckedTypeWellKeys : setCheckedForecastKeys;
      if (useMcDanielForecasts && checkedKeys.indexOf(MCDANIEL_FOLDER_ID) === -1) {
        setKeys([...checkedKeys, MCDANIEL_FOLDER_ID]);
      }
      if (useNoviForecasts && checkedKeys.indexOf(NOVI_FOLDER_ID) === -1) {
        setKeys([...checkedKeys, NOVI_FOLDER_ID]);
      }
      if (useWorkingForecasts && checkedKeys.indexOf(WORKING_FORECAST_FOLDER_ID) === -1) {
        setKeys([...checkedKeys, WORKING_FORECAST_FOLDER_ID]);
      }
    },
    [
      checkedKeys,
      useMcDanielForecasts,
      useNoviForecasts,
      useWorkingForecasts,
      setCheckedTypeWellKeys,
      setCheckedForecastKeys
    ]
  );

  const getForecastFoldersFromFoldersData = useCallback(
    (data, type: ForecastActivityType) => {
      // Process each folder based on type
      const userArps =
        data?.folders?.flatMap((folder) => {
          // Empty folder handling
          if (!folder.forecasts || folder.forecasts.length === 0) {
            return [createEmptyFolder(folder)];
          }

          // Process based on type
          if (type === USER_ARPS) {
            return processUserArpsFolder(folder);
          } else if (type === TYPE_WELLS) {
            return [processTypeWellsFolder(folder)];
          }

          return [];
        }) || [];

      // USER_ARPS specific: add special folders and sort
      if (type === USER_ARPS) {
        const specialFolders = getSpecialFoldersFromPreferences(forecastPreference);
        userArps.push(...specialFolders);

        syncSpecialFolderCheckedKeys(type);

        return sortFoldersByPriority(userArps);
      }

      return userArps;
    },
    [forecastPreference, syncSpecialFolderCheckedKeys]
  );

  return { getForecastFoldersFromFoldersData };
}
